import type { GridRowId, GridTreeNode, ReorderValidationContext } from '@mui/x-data-grid-pro';

export const isLevelCompatible = (
  sourceLevel: number | undefined,
  targetLevel: number | undefined,
  sourceDepth: number,
  targetDepth: number,
): boolean =>
  sourceLevel !== undefined && targetLevel !== undefined
    ? sourceLevel === targetLevel
    : sourceDepth === targetDepth;

export type SelectionMember = {
  level: number | undefined;
  isUnparented: boolean;
  depth: number;
};

const shareConfiguredLevel = (first: SelectionMember, rest: SelectionMember[]): boolean =>
  first.level !== undefined && rest.every((m) => m.level === first.level);

const shareDepthWithoutLevels = (first: SelectionMember, rest: SelectionMember[]): boolean =>
  first.level === undefined && rest.every((m) => m.level === undefined && m.depth === first.depth);

const allUnparented = (first: SelectionMember, rest: SelectionMember[]): boolean =>
  first.isUnparented && rest.every((m) => m.isUnparented);

export const isSelectionLevelCompatible = (members: SelectionMember[]): boolean => {
  const [first, ...rest] = members;
  if (!first) return true;
  return (
    shareConfiguredLevel(first, rest) ||
    shareDepthWithoutLevels(first, rest) ||
    allUnparented(first, rest)
  );
};

export type SiblingReorderContext = Pick<
  ReorderValidationContext,
  'sourceNode' | 'targetNode' | 'dropPosition' | 'prevNode' | 'nextNode'
>;

export const isNoOpReorder = (context: SiblingReorderContext): boolean => {
  if (context.sourceNode.id === context.targetNode.id) {
    return true;
  }
  if (context.dropPosition === 'above' && context.prevNode?.id === context.sourceNode.id) {
    return true;
  }
  if (context.dropPosition === 'below' && context.nextNode?.id === context.sourceNode.id) {
    return true;
  }
  return false;
};

type ReparentContext = {
  sourceNode: GridTreeNode;
  targetNode: GridTreeNode;
  sourceTypeLevelOrder?: number;
  targetTypeLevelOrder?: number;
  selectionParentIds?: (GridRowId | null)[];
};

export const validateParentReparent = ({
  sourceNode,
  targetNode,
  sourceTypeLevelOrder,
  targetTypeLevelOrder,
  selectionParentIds,
}: ReparentContext): boolean => {
  const parentIds = selectionParentIds?.length ? selectionParentIds : [sourceNode.parent];
  const everyDraggedRowAlreadyUnderTarget = parentIds.every(
    (parentId) => parentId === targetNode.id,
  );
  if (everyDraggedRowAlreadyUnderTarget) return false;
  if (sourceTypeLevelOrder !== undefined && targetTypeLevelOrder !== undefined) {
    return targetTypeLevelOrder < sourceTypeLevelOrder;
  }
  // Depth fallback only works for non-root sources: root items (depth 0) need type data.
  if (sourceNode.depth > 0) {
    return targetNode.depth < sourceNode.depth;
  }
  return false;
};

export const validateSiblingReorder = (context: SiblingReorderContext): boolean => {
  if (context.dropPosition === 'inside') {
    return false;
  }
  if (isNoOpReorder(context)) {
    return false;
  }
  return context.sourceNode.parent === context.targetNode.parent;
};
